import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, NgZone, OnInit, ViewChild, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { StoreBase } from "../../stores/models/store.model";
import { Tag } from "../../tags/models/tag.model";
import { GroupWithItems } from "../../store-groups/models/group-item.model";
import { StoreApiService } from "../../stores/services/store.resource";
import { PriceChange, Product, ProductGroup, ReportCategory, PersistedReportCategories } from "../models/price-change.model";
import { StoreSelectionDialogData, ReportCategorySelectionDialogData, ColumnSelectionDialogData, SubmitPriceChangeDialogData, SetPriceDialogData } from "../models/dialog.model";
import { PriceChangeService } from "../services/price-change.resource";
import { PriceChangeReportCategorySelectionDialog } from "./dialogs/report-category-selection.component";
import { PriceChangeStoreSelectionDialog } from "./dialogs/store-selection.component";
import {
    ColDef, GridApi, GridReadyEvent, ValueFormatterParams, CellContextMenuEvent, ITextFilterParams,
    INumberFilterParams, GridOptions, themeQuartz, colorSchemeDarkBlue, colorSchemeDarkWarm, colorSchemeLightWarm, colorSchemeLightCold,
    colorSchemeDark,
} from 'ag-grid-community';
import { PriceChangeColumnSelectionDialog } from "./dialogs/column-selection.component";
import { SubmitPriceChangeDialog } from "./dialogs/submit-price-change.component";
import { CdkContextMenuTrigger } from "@angular/cdk/menu";
import { PriceChangeRowCompDialog } from "./dialogs/row-comp-dialog.component";
import { StoreGroupApiService } from "../../store-groups/services/store-group.resource";
import { TagApiService } from "../../tags/services/tag.resource";
import { IconCellRenderer } from "./IconCellRenderer";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { SetPriceDialog } from "./dialogs/set-price.component";
import { BrowserStorage } from "../../../utils/shared-services/browser-storage.service";
import { Observable } from "rxjs";

@Component({
    selector: "app-price-change",
    templateUrl: "./src/app/features/price-change/templates/price-change.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PriceChangeComponent implements OnInit {
    light_theme = themeQuartz.withPart(colorSchemeLightWarm);
    dark_theme = themeQuartz.withPart(colorSchemeDark);
    private conceptID: number
    private categorySelectedBy: "Descript" | "PLink"
    private reportCategories: ReportCategory[]
    private maxCategoryGroupSelection: number
    private products: Product[]
    private gridApi!: GridApi<ProductGroup>

    private modifiedCells: Map<string, boolean>;

    @ViewChild(CdkContextMenuTrigger)
    private contextMenuTrigger!: CdkContextMenuTrigger;

    public captions: Record<string, string>;
    public stores: StoreBase[];
    public selectedStores: StoreBase[];
    public storeGroups: GroupWithItems[];
    public tags: Tag[];
    public selectedCategories: ReportCategory[];
    public numCategoryGroupsSelected: number;
    public groupBy: string;
    public rowData: ProductGroup[]; // The data to be displayed.
    public colDefs: ColDef[] // Defines the columns to be displayed.
    public numColsSelected: number;
    public filterText: string;
    public priceChange: PriceChange = {
        info: { PriceChangeDate: '', RollbackDate: '' },
        rollbackPrices: [],
        changedPrices: [],
        label: ''
    };
    public nodeIds: String[] = [];
    public contextMenuEvent: CellContextMenuEvent;
    public gridOptions: GridOptions
    public hideApplyPriceToRow: boolean = false;
    public orgProducts: Product[] = [];
    isDoneProcessing: boolean = true;
    processedCount: number = 0;
    public totalCount = 0;
    theme: any;
    gridTheme: any;
    public isPageLoading = true;
    public isGridReady = false;


    constructor(
        @Inject(StoreApiService) private storeApiService: StoreApiService,
        @Inject(StoreGroupApiService) private storeGroupApiService: StoreGroupApiService,
        @Inject(TagApiService) private tagApiService: TagApiService,
        @Inject(NgxIndexedDBService) private dbService: NgxIndexedDBService,
        @Inject(ActivatedRoute) private actRoute: ActivatedRoute,
        @Inject(PriceChangeService) private priceChangeService: PriceChangeService,
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(ChangeDetectorRef) private cd: ChangeDetectorRef,
        @Inject(BrowserStorage) private browserStorage: BrowserStorage,
        @Inject(NgZone) private ngZone: NgZone
    ) {
        this.actRoute.data.subscribe(data => {
            this.captions = data.captions;
        });
        this.theme = this.browserStorage.getLocalstorage('appTheme', null);
        this.conceptID = null;
        this.stores = [];
        this.selectedStores = [];
        this.reportCategories = [];
        this.selectedCategories = [];
        this.categorySelectedBy = null;
        this.maxCategoryGroupSelection = 5;
        this.numCategoryGroupsSelected = 0;
        this.groupBy = "Descript";
        this.rowData = [];
        this.products = [];
        this.modifiedCells = new Map<string, boolean>();


        this.colDefs = [
            {
                headerName: this.captions.frontDescription, field: "Descript", cellStyle: { textAlign: "right" }, filter: "agTextColumnFilter", valueFormatter: this.nullFormatter, filterParams: {
                    buttons: ["reset"],
                } as ITextFilterParams
            },
            {
                headerName: "PLink", field: "PLink", cellStyle: { textAlign: "right" }, filter: "agTextColumnFilter", valueFormatter: this.nullFormatter, filterParams: {
                    buttons: ["reset"],
                } as ITextFilterParams
            },
            {
                headerName: this.captions.stores, field: "Stores", cellStyle: { textAlign: "right" }, filter: "agTextColumnFilter", tooltipValueGetter: () => this.getProductToolTip(),
                cellRenderer: IconCellRenderer,
                filterParams: {
                    buttons: ["reset"],
                } as ITextFilterParams,
                valueGetter: params => {
                    const products = params.data.products as Product[];
                    const uniqueStoreIDs = new Set();

                    products.forEach(product => {
                        uniqueStoreIDs.add(product.StoreID);
                    });

                    return (this.groupBy == "PLink") ? uniqueStoreIDs.size + "/" + this.selectedStores.length + " " + products.length : uniqueStoreIDs.size + "/" + this.selectedStores.length;
                }
            },
            {
                headerName: "PriceA", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", filterParams: {
                    buttons: ["reset"],
                } as INumberFilterParams, editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceA"),
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceA: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceA"] = params.newValue != null ? params.newValue : product["PriceA"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceA"]
                }
            },
            {
                headerName: "PriceB", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceB"),
                filterParams: {
                    buttons: ["reset"],
                } as INumberFilterParams,
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceB: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceB"] = params.newValue != null ? params.newValue : product["PriceB"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceB"]
                }
            },
            {
                headerName: "PriceC", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceC"),
                filterParams: {
                    buttons: ["reset"],
                } as INumberFilterParams,
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceC: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceC"] = params.newValue != null ? params.newValue : product["PriceC"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceC"]
                }
            },
            {
                headerName: "PriceD", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceD"),
                filterParams: {
                    buttons: ["reset"],
                } as INumberFilterParams,
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceD: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceD"] = params.newValue != null ? params.newValue : product["PriceD"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceD"]
                }
            },
            {
                headerName: "PriceE", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceE"),
                filterParams: {
                    buttons: ["reset"],
                } as INumberFilterParams,
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceE: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceE"] = params.newValue != null ? params.newValue : product["PriceE"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceE"]
                }
            },
            {
                headerName: "PriceF", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceF"),
                filterParams: {
                    buttons: ["reset"],
                } as INumberFilterParams,
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        if (params.newValue == null) params.newValue = product["PriceF"]
                        let productNewPrice = { PriceF: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceF"] = params.newValue != null ? params.newValue : product["PriceF"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceF"]
                }
            },
            {
                headerName: "PriceG", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceG"),
                filterParams: {
                    buttons: ["reset"],
                } as INumberFilterParams,
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceG: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceG"] = params.newValue != null ? params.newValue : product["PriceG"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceG"]
                }
            },
            {
                headerName: "PriceH", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceH"),
                filterParams: {
                    buttons: ["reset"],
                } as INumberFilterParams,
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceH: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceH"] = params.newValue != null ? params.newValue : product["PriceH"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceH"]
                }
            },
            {
                headerName: "PriceI", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceI"),
                filterParams: {
                    buttons: ["reset"],
                } as INumberFilterParams,
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceI: params.newValue as number, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceI"] = params.newValue != null ? params.newValue : product["PriceI"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceI"]
                }
            },
            {
                headerName: "PriceJ", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceJ"),
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceJ: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceJ"] = params.newValue != null ? params.newValue : product["PriceJ"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceJ"]
                }
            },
            {
                headerName: "Price MOD", field: "products", cellStyle: { textAlign: "right" }, cellDataType: 'number', filter: "agNumberColumnFilter", editable: true, valueFormatter: this.groupPriceFormatter.bind(null, "PriceMode"),
                cellClass: (params) => {
                    // Generate a unique key for the cell based on the rowIndex and column field
                    const cellKey = `${params.node.rowIndex}-${params.column.getColId()}`;
                    return this.modifiedCells.get(cellKey) ? 'bg-highlight-color' : null;
                },
                valueSetter: params => {
                    for (let product of params.data.products) {
                        let productNewPrice = { PriceMode: params.newValue, ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
                        this.addProduct(productNewPrice, false);
                        product["PriceMode"] = params.newValue != null ? params.newValue : product["PriceMode"];
                    }
                    return true;
                },
                valueGetter: params => {
                    return params.data.products[0]["PriceMode"]
                }
            }
        ];

        this.numColsSelected = this.colDefs.length;

        this.gridOptions = {
            columnDefs: this.colDefs,
            rowData: this.rowData,
        };
    }

    onCellValueChanged(ev) {
        if (ev.oldValue != ev.newValue) {
            const cellKey = `${ev.node.rowIndex}-${ev.column.colId}`;
            this.modifiedCells.set(cellKey, true);
            ev.api.refreshCells({ rowNodes: [ev.node], columns: [ev.column.colId], force: true });
        }
    }

    private async addProduct(newProduct: any, isApplyRow: boolean): Promise<void> {
        // Check and update or remove the product in the changedPrices array
        await this.checkUpdateOrRemoveProduct(this.priceChange.changedPrices, newProduct, isApplyRow);
    }

    private checkUpdateOrRemoveProduct(
        productsArray: any[],
        product: any,
        isApplyRow: boolean
    ): void {
        const existingIndex = productsArray.findIndex(
            p => p.StoreID === product.StoreID && p.ProdNum === product.ProdNum
        );

        // If we don’t have orgProducts in memory, we can’t compare — bail early
        if (!this.orgProducts || this.orgProducts.length === 0) {
            console.warn("orgProducts not loaded in memory yet!");
            return;
        }

        if (existingIndex !== -1) {
            // lookup original product
            const original = this.orgProducts.find(
                p => p.StoreID === product.StoreID && p.ProdNum === product.ProdNum
            );

            if (!original) {
                // fallback: no match found in orgProducts
                Object.assign(productsArray[existingIndex], product);
                return;
            }

            const priceKeys = Object.keys(product).filter(k => k.startsWith("Price"));
            let removeProduct = false;

            for (const key of priceKeys) {
                if (original[key] === product[key]) {
                    // If product has only StoreID/ProdNum + this one price, remove it entirely (unless isApplyRow)
                    if (Object.keys(productsArray[existingIndex]).length === 3 && !isApplyRow) {
                        productsArray.splice(existingIndex, 1);
                    } else {
                        delete productsArray[existingIndex][key];
                    }
                    removeProduct = true;
                }
            }

            // Update if not removed
            if (!removeProduct) {
                Object.assign(productsArray[existingIndex], product);
            }
        } else {
            // If product not found in changed array, add it
            productsArray.push(product);
        }
    }


    private createRollbackArray() {
        // reset rollback prices
        this.priceChange.rollbackPrices = [];

        // build a quick lookup for orgProducts (StoreID + ProdNum)
        const orgProductsMap = new Map(
            this.orgProducts.map(p => [`${p.StoreID}-${p.ProdNum}`, p])
        );

        this.priceChange.changedPrices.forEach(changedProduct => {
            const orgProduct = orgProductsMap.get(`${changedProduct.StoreID}-${changedProduct.ProdNum}`);
            if (!orgProduct) return;

            const rollbackProduct: any = {
                ProdNum: changedProduct.ProdNum,
                StoreID: changedProduct.StoreID
            };

            // copy original values for all price columns that were changed
            for (const propName of Object.keys(changedProduct)) {
                if (propName.startsWith("Price")) {
                    rollbackProduct[propName] = orgProduct[propName];
                }
            }

            this.priceChange.rollbackPrices.push(rollbackProduct);
        });
    }


    ngOnInit(): void {
        this.actRoute.params.subscribe(params => {
            this.conceptID = +params['pathId'];
        });

        if (this.theme == "light")
            this.gridTheme = this.light_theme;
        else this.gridTheme = this.dark_theme;

        this.stores = history.state.stores || [];
        this.storeGroups = history.state.storeGroups || [];
        this.tags = history.state.tags || [];
        this.selectedStores = history.state.selectedStores || [];

        const storePromises: Promise<any>[] = [];
        if (this.stores.length <= 0) {
            storePromises.push(this.storeApiService.getStoresByConceptId(this.conceptID).then(stores => this.stores = stores));
        }
        if (this.storeGroups.length <= 0) {
            storePromises.push(this.storeGroupApiService.getAllStoreGroupsWithItemsForConcept(this.conceptID)
                .then(groups => this.storeGroups = groups));
        }
        if (this.tags.length <= 0) {
            storePromises.push(this.tagApiService.getAllTagsForConcept(this.conceptID).then(tags => this.tags = tags));
        }

        // Wait for all static data to load first
        Promise.all(storePromises).then(() => {
            if (this.selectedStores.length > 0) {
                this.loadReportCategoriesAndProducts();
            } else {
                this.isPageLoading = false; // nothing to load
            }
        });

    }


    private loadReportCategoriesAndProducts(): void {
        this.dbService.getByKey('reportCategories', 1).subscribe(saved => {
            const savedCategories = saved as PersistedReportCategories | undefined;
            if (savedCategories) {
                this.reportCategories = savedCategories.categories;
                this.selectedCategories = savedCategories.selected || [];
                this.numCategoryGroupsSelected = history.state.numCategoryGroupsSelected || 0;
                this.categorySelectedBy = history.state.categorySelectedBy;
                this.groupBy = history.state.groupBy;

                // get products from indexedDB
                this.dbService.getAll('orgProducts').subscribe((orgProductChunks: any[]) => {
                    const savedProducts = orgProductChunks.flatMap(chunk => chunk.products);
                    if (savedProducts) {
                        this.products = savedProducts || [];
                        this.orgProducts = savedProducts;
                        // Make sure grid picks up the data
                        if (this.gridApi) {
                            this.applyGroupBy(this.groupBy);
                        }
                        this.isPageLoading = false;
                        this.cd.detectChanges();

                    }

                });

            }
            else {
                let storeIDs = this.selectedStores.map(store => store.id);
                this.priceChangeService.getReportCategories(storeIDs).then(reportCats => {
                    this.reportCategories = reportCats;
                    this.isPageLoading = false;
                });

            }
        });

    }

    onGridReady(params: GridReadyEvent<ProductGroup>): void {
        this.gridApi = params.api;
        this.applyGroupBy(this.groupBy);
        this.isPageLoading = false;
    }

    onFilterTextBoxChanged() {
        this.gridApi.setGridOption('quickFilterText', this.filterText);
    }

    openStoreSelection(): void {
        const dialogRef = this.dialog.open(PriceChangeStoreSelectionDialog, {
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '95vw',
            data: {
                stores: this.stores,
                storeGroups: this.storeGroups,
                tags: this.tags
            },
        });

        dialogRef.afterClosed().subscribe((result: StoreSelectionDialogData) => {
            if (result) {
                // Set store selection
                this.stores = result.stores;
                this.selectedStores = result.selectedStores;

                // Persist store selection
                history.replaceState({
                    stores: this.stores,
                    selectedStores: this.selectedStores,
                }, null);

                if (this.selectedStores.length > 0) {
                    // Reset and Fetch report categories
                    let storeIDs = this.selectedStores.map(store => store.id);
                    this.priceChangeService.getReportCategories(storeIDs).then(reportCats => {
                        this.reportCategories = reportCats;
                    });
                    this.categorySelectedBy = null;
                    this.numCategoryGroupsSelected = 0;
                    this.selectedCategories = [];

                    // Reset products
                    this.products = [];
                    this.rowData = [];
                    this.dbService.clear('orgProducts').subscribe(() => { });

                }
            }
            this.cd.detectChanges();
        });
    }

    openReportCategorySelection(): void {
        const dialogRef = this.dialog.open(PriceChangeReportCategorySelectionDialog, {
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '95vw',
            data: {
                selectedStoresLength: this.selectedStores.length,
                reportCategories: this.reportCategories,
                selectedBy: this.categorySelectedBy,
                maxSelection: this.maxCategoryGroupSelection,
                numSelected: this.numCategoryGroupsSelected,
                isPriceMatch: false
            },
        });

        dialogRef.afterClosed().subscribe((result: ReportCategorySelectionDialogData) => {
            if (result) {

                // Set report category selection
                this.categorySelectedBy = result.selectedBy;
                this.numCategoryGroupsSelected = result.numSelected;
                this.reportCategories = result.reportCategories;
                this.selectedCategories = result.reportCategories.filter(rc => {
                    return rc.isSelected;
                });


                // Persist report categories in IndexedDB
                this.dbService.clear('reportCategories').subscribe(() => {
                    this.dbService.add('reportCategories', {
                        id: 1,
                        categories: this.reportCategories,
                        selected: this.selectedCategories
                    }).subscribe(() => {
                    });
                });

                // Persist report category selection
                history.replaceState({
                    ...history.state,
                    categorySelectedBy: this.categorySelectedBy,
                    numCategoryGroupsSelected: this.numCategoryGroupsSelected,
                }, null);

                // Fetch products
                this.priceChangeService.getProducts(this.selectedCategories).then(result => {
                    this.products = result;

                    // ✅ Keep in-memory orgProducts
                    this.orgProducts = [...result];

                    // want to clear any objects in db first, then add new results
                    const chunkSize = 10000; // Define the chunk size
                    this.dbService.clear('orgProducts').subscribe(() => {
                        for (let i = 0; i < this.products.length; i += chunkSize) {
                            const chunk = this.products.slice(i, i + chunkSize);
                            this.dbService.add('orgProducts', { id: i / chunkSize, products: chunk }).subscribe();
                        }
                    });

                    // reset price change array, cell colours and submit button
                    this.priceChange = {
                        info: { PriceChangeDate: '', RollbackDate: '' },
                        rollbackPrices: [],
                        changedPrices: [],
                        label: ''
                    };
                    this.modifiedCells.clear(); // Clear all modifications tracking
                    this.gridApi.redrawRows();

                    this.cd.detectChanges();
                    this.applyGroupBy(this.groupBy);

                });
            }
            this.cd.detectChanges();
        });
    }

    applyGroupBy(groupBy: string): void {
        this.groupBy = groupBy;

        // Persist group by data
        history.replaceState({
            ...history.state,
            groupBy: this.groupBy,
        }, null);

        // Group products into rowData
        this.rowData = this.groupProducts(this.products, groupBy);

        // Update grid with rowData
        this.gridApi.setGridOption('rowData', this.rowData);

        //Update columns
        if (groupBy === "Descript") {
            this.gridApi.setColumnsVisible(["Stores"], true);
            this.colDefs.find(c => c.headerName === "Description").hide = false;
            this.colDefs.find(c => c.headerName === "PLink").hide = true;
        }
        else if (groupBy === "PLink") {
            this.gridApi.setColumnsVisible(["Stores"], true);
            this.colDefs.find(c => c.headerName === "Description").hide = true;
            this.colDefs.find(c => c.headerName === "PLink").hide = false;
        }
        else {
            this.gridApi.setColumnsVisible(["Stores"], false);
            this.colDefs.find(c => c.headerName === "Description").hide = false;
            this.colDefs.find(c => c.headerName === "PLink").hide = false;
        }
        this.numColsSelected = this.colDefs.filter(c => !c.hide).length;
        this.gridApi.setGridOption("columnDefs", this.colDefs);
    }

    openColumnSelection(): void {
        const dialogRef = this.dialog.open(PriceChangeColumnSelectionDialog, {
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '95vw',
            data: {
                columns: this.colDefs,
                groupBy: this.groupBy
            },
        });

        dialogRef.afterClosed().subscribe((result: ColumnSelectionDialogData) => {
            if (result) {
                this.numColsSelected = result.columns.filter(c => !c.hide).length;
                this.gridApi.setGridOption("columnDefs", result.columns);
            }
        });


    }

    submitDialog(): void {
        this.createRollbackArray();
        const dialogRef = this.dialog.open(SubmitPriceChangeDialog, {
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '95vw',
            minWidth: '25%',
            data: {
                priceChange: this.priceChange,
                selectedStores: this.selectedStores,
                conceptID: this.conceptID
            },
        });
        dialogRef.afterClosed().subscribe((result: SubmitPriceChangeDialogData) => {
            if (result) {
                this.priceChangeService.updatePrices(result.priceChange);
                setTimeout(() => {
                    this.resetSelection(this.priceChange);
                }, 2000);
            }
        });
    }

    resetSelection(priceChange: PriceChange): void {
        priceChange.info = { RollbackDate: '', PriceChangeDate: '' };
        priceChange.rollbackPrices = [];
        priceChange.changedPrices = [];
        this.modifiedCells.clear(); // Clear all modifications tracking
        this.gridApi.redrawRows(); // Redraw all rows to reset styles
        this.cd.detectChanges();
    }

    openCellMenu(contextMenuEvent: CellContextMenuEvent): void {
        this.contextMenuEvent = contextMenuEvent;

        // check if a cell is in edit mode and do not open menu, if not can open menu
        const rowNode = this.contextMenuEvent.node;
        const colId = this.contextMenuEvent.column.getId();
        const cellEditorInstances = this.gridApi.getCellEditorInstances({
            rowNodes: [rowNode],
            columns: [colId]
        });
        if (cellEditorInstances.length > 0) {
            this.contextMenuEvent.event.preventDefault();
            this.contextMenuEvent.event.stopPropagation();
        } else {


            const x = (this.contextMenuEvent.event as PointerEvent)?.pageX + 1;
            const y = (this.contextMenuEvent.event as PointerEvent)?.pageY + 1;

            // hide apply Price to Row if clicked on Decript/Plink/Store columns
            if (this.contextMenuEvent.colDef.field != 'products') {
                this.hideApplyPriceToRow = true;
                // hide apply Price to Row if clicked on PRICE MOD
            } else if (this.contextMenuEvent.colDef.headerName == "Price MOD") this.hideApplyPriceToRow = true;
            else this.hideApplyPriceToRow = false

            //  only groupby condition will show row comp option, other options will always show
            this.contextMenuTrigger.open({ x, y });

        }
    }

    viewRowComposition(rowData: ProductGroup): void {
        this.dialog.open(PriceChangeRowCompDialog, {
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '40vw',
            data: rowData
        });
    }

    applyPricesToRow(rowData: ProductGroup): void {
        const value = this.contextMenuEvent.value;

        const newProducts: Product[] = [];

        // Precompute which price columns are visible
        const visiblePriceCols = this.colDefs
            .filter(col => col.headerName?.startsWith("Price") && !col.hide && col.headerName !== "Price MOD")
            .map(col => col.headerName as keyof Product); // ["PriceA", "PriceB", ...]


        rowData.products.forEach(product => {
            // Clone with updated prices
            const updated: Product = {
                ...product,
                ...Object.fromEntries(
                    visiblePriceCols.map(col => [col, value])
                )
            };

            newProducts.push(updated);

            const changedProduct: Partial<Product> = {
                StoreID: product.StoreID,
                ProdNum: product.ProdNum
            };
            for (const col of visiblePriceCols) {
                (changedProduct as any)[col] = value;
            }

            // Update changedPrices array in bulk
            this.updateChangedPrices(changedProduct);

        });

        // Update modified cells
        this.gridApi.getColumns()?.forEach((column: any) => {
            if (!column.colDef.hide && column.colDef.headerName !== "Price MOD") {
                const colId = column.getColId();
                const cellKey = `${this.contextMenuEvent.node.rowIndex}-${colId}`;
                this.modifiedCells.set(cellKey, true);
                this.gridApi.refreshCells({ rowNodes: [this.contextMenuEvent.node], columns: [colId], force: true });
            }
        });

        // Replace row data
        this.contextMenuEvent.node.setData({
            StoreID: this.contextMenuEvent.data.StoreID,
            Descript: this.contextMenuEvent.data.Descript,
            products: newProducts
        });

        this.gridApi.refreshCells({ force: true });

    }

    private updateChangedPrices(product: Partial<Product>): void {
        if (!this.orgProducts || this.orgProducts.length === 0) return;

        const idx = this.priceChange.changedPrices.findIndex(
            p => p.StoreID === product.StoreID && p.ProdNum === product.ProdNum
        );

        const original = this.orgProducts.find(
            p => p.StoreID === product.StoreID && p.ProdNum === product.ProdNum
        );
        if (!original) return;

        const priceKeys = Object.keys(product).filter(k => k.startsWith("Price"));

        if (idx !== -1) {
            for (const key of priceKeys) {
                const newVal = Number(product[key]).toFixed(2);
                const origVal = Number(original[key]).toFixed(2);

                if (newVal === origVal) {
                    if (Object.keys(this.priceChange.changedPrices[idx]).length === 2) {
                        // only StoreID + ProdNum left
                        this.priceChange.changedPrices.splice(idx, 1);
                        return
                    } else {
                        delete this.priceChange.changedPrices[idx][key];
                    }
                }
            }

            Object.assign(this.priceChange.changedPrices[idx], product);
        } else {
            this.priceChange.changedPrices.push(product);
        }
    }



    async applyPriceToColumn(): Promise<void> {
        // ensure gridApi ready
        if (!this.gridApi) return;

        const value = this.contextMenuEvent.value;
        const colHeaderName = this.contextMenuEvent.colDef.headerName as keyof Product;
        if (!colHeaderName) return;

        // collect all current data snapshot from grid
        const nodes: any[] = [];
        this.gridApi.forEachNode(node => nodes.push(node));

        // setup progress UI
        this.processedCount = 0;
        this.totalCount = nodes.reduce((acc, n) => acc + (n.data?.products?.length || 0), 0);
        this.isDoneProcessing = false;
        document.body.classList.add('overlay-active');
        this.cd.detectChanges();

        // let browser render overlay
        await new Promise(r => setTimeout(r, 0));

        // We'll build a new dataset (fast JS objects) outside angular
        const updatedRowData: any[] = new Array(nodes.length);
        // We'll also build a Map for changed prices keyed by `${StoreID}|${ProdNum}`
        const changedMap = new Map<string, any>();

        const CHUNK_ROWS = 100; // tune: larger = faster but more blocking; smaller = smoother UI
        // do heavy work outside angular
        await this.ngZone.runOutsideAngular(async () => {
            for (let start = 0; start < nodes.length; start += CHUNK_ROWS) {
                const slice = nodes.slice(start, start + CHUNK_ROWS);

                for (let i = 0; i < slice.length; i++) {
                    const node = slice[i];
                    const products: Product[] = node.data?.products || [];

                    // Map products quickly (no Angular or CD)
                    const updatedProducts = products.map(p => {
                        // create new object with the single column changed
                        const updated = { ...p, [colHeaderName]: value } as Product;
                        // accumulate changed price (key)
                        const key = `${p.StoreID}|${p.ProdNum}`;
                        const changedEntry = changedMap.get(key) || { StoreID: p.StoreID, ProdNum: p.ProdNum };
                        // store only the column we changed (avoid cloning heavy objects)
                        changedEntry[colHeaderName] = value;
                        changedMap.set(key, changedEntry);
                        return updated;
                    }) as [Product, ...Product[]]; // we'll ensure non-empty below when applying

                    // preserve tuple shape safely: if products empty, skip (shouldn't happen)
                    if (updatedProducts.length === 0) {
                        // keep original data if somehow empty
                        updatedRowData[start + i] = node.data;
                    } else {
                        // put new ProductGroup object (no AG Grid calls here)
                        updatedRowData[start + i] = {
                            ...node.data,
                            products: [updatedProducts[0], ...updatedProducts.slice(1)]
                        };
                    }

                    // update processed count every product group (we'll surface it periodically)
                    this.processedCount += products.length;
                }

                // yield to browser occasionally so it can repaint (still outside Angular)
                // we now schedule to update UI on main thread:
                await new Promise<void>(resolve => {
                    // return to Angular to update progress UI
                    this.ngZone.run(() => {
                        // cap processedCount for UI safety
                        if (this.processedCount > this.totalCount) this.processedCount = this.totalCount;
                        this.cd.detectChanges(); // update progress text & bar
                        // yield a tick so browser repaints
                        setTimeout(() => resolve(), 0);
                    });
                });
            }
        });

        // Done building updatedRowData & changedMap. Now commit back ONCE inside Angular
        this.ngZone.run(() => {
            // replace the entire grid data in one shot (fast)
            this.gridApi.setGridOption('rowData', updatedRowData);

            // Efficient merge of changedMap into existing changedPrices
            const existingMap = new Map<string, any>(
                this.priceChange.changedPrices.map(p => [`${p.StoreID}|${p.ProdNum}`, p])
            );

            for (const [key, newEntry] of changedMap.entries()) {
                if (existingMap.has(key)) {
                    // update only the changed column
                    existingMap.get(key)[colHeaderName] = newEntry[colHeaderName];
                } else {
                    // insert new entry
                    existingMap.set(key, newEntry);
                }
            }

            // rebuild changedPrices in one pass
            this.priceChange.changedPrices = Array.from(existingMap.values());



            // mark modified cells for UI highlighting (one pass)
            // optional: compute column id for target column
            const targetCol = this.gridApi.getColumns()?.find((c: any) => c.colDef.headerName === colHeaderName);
            const targetColId = targetCol ? targetCol.getColId() : null;
            if (targetColId) {
                // mark every row as modified in the map for this column
                this.gridApi.forEachNode((n) => {
                    const key = `${n.rowIndex}-${targetColId}`;
                    this.modifiedCells.set(key, true);
                });

                // 🔑 Force AG Grid to refresh that column so yellow shows immediately
                this.gridApi.refreshCells({
                    force: true,
                    columns: [targetColId],
                });
            }

            // final UI cleanup
            this.isDoneProcessing = true;
            document.body.classList.remove('overlay-active');
            this.cd.detectChanges();
        });
    }

    resetRowPrices(rowData: ProductGroup) {
        let newProduct: Product[] = [];
        // get the org products
        this.dbService.getAll('orgProducts').subscribe((orgProductsChunks: any[]) => {
            this.orgProducts = orgProductsChunks.flatMap(chunk => chunk.products);
            // loop through products of row 
            rowData.products.forEach(product => {
                // find the product in changed products array and in original array 
                let existingProductIndex = this.priceChange.changedPrices.findIndex(p => p.StoreID === product.StoreID && p.ProdNum === product.ProdNum);
                let originalProductIndex = this.orgProducts.findIndex(p => p.StoreID === product.StoreID && p.ProdNum === product.ProdNum);

                // remove product from changed products 
                if (existingProductIndex !== -1) {
                    this.priceChange.changedPrices.splice(existingProductIndex, 1);
                }

                // put the orginal values back in 
                if (originalProductIndex !== -1) {
                    newProduct.push({
                        StoreID: product.StoreID,
                        ProdNum: product.ProdNum,
                        Descript: product.Descript,
                        PLink: product.PLink,
                        PriceA: this.orgProducts[originalProductIndex].PriceA,
                        PriceB: this.orgProducts[originalProductIndex].PriceB,
                        PriceC: this.orgProducts[originalProductIndex].PriceC,
                        PriceD: this.orgProducts[originalProductIndex].PriceD,
                        PriceE: this.orgProducts[originalProductIndex].PriceE,
                        PriceF: this.orgProducts[originalProductIndex].PriceF,
                        PriceG: this.orgProducts[originalProductIndex].PriceG,
                        PriceH: this.orgProducts[originalProductIndex].PriceH,
                        PriceI: this.orgProducts[originalProductIndex].PriceI,
                        PriceJ: this.orgProducts[originalProductIndex].PriceJ,
                        PriceMode: product.PriceMode
                    })
                }

            })

            // assign the orginal products back to row and remove yellow background
            let columns = this.gridApi.getColumns();
            columns.forEach(column => {
                const colId = column.getColId(); // Get column ID safely
                const cellKey = `${this.contextMenuEvent.node.rowIndex}-${colId}`;
                this.modifiedCells.set(cellKey, false);
                this.gridApi.refreshCells({ rowNodes: [this.contextMenuEvent.node], columns: [colId], force: true });
            })

            this.contextMenuEvent.node.setData({
                StoreID: this.contextMenuEvent.data.StoreID,
                Descript: this.contextMenuEvent.data.Descript,
                products: newProduct
            })

            this.gridApi.refreshCells({ force: true });

        });

    }


    setRowPrice(rowData: ProductGroup) {
        const dialogRef = this.dialog.open(SetPriceDialog, {
            data: { isRow: true },
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '95vw',
        });

        dialogRef.afterClosed().subscribe((result: SetPriceDialogData) => {
            if (!result) return;

            const rowChange = result.change;
            const isPercentage = rowChange.dropdown === 'Percentage';
            const floatInput = parseFloat(rowChange.inputBox);

            // Precompute which price columns are visible
            const visiblePriceCols = this.colDefs
                .filter(col => col.headerName?.startsWith("Price") && !col.hide && col.headerName !== "Price MOD")
                .map(col => col.headerName as keyof Product);

            const newProducts: Product[] = [];

            rowData.products.forEach(product => {
                // compute new values for visible columns
                const updatedCols = Object.fromEntries(
                    visiblePriceCols.map(col => {
                        const currentVal = Number(product[col]) || 0;
                        const newVal = this.applyChange(rowChange.actionType, floatInput, currentVal, isPercentage);
                        this.createProductObject(col.replace("Price", ""), newVal, product); // update changedPrices
                        return [col, newVal];
                    })
                );

                const updated: Product = { ...product, ...updatedCols };
                newProducts.push(updated);
            });

            // Mark modified cells for the row
            this.gridApi.getColumns()?.forEach((column: any) => {
                if (!column.colDef.hide && column.colDef.headerName !== "Price MOD") {
                    const colId = column.getColId();
                    const cellKey = `${this.contextMenuEvent.node.rowIndex}-${colId}`;
                    this.modifiedCells.set(cellKey, true);
                    this.gridApi.refreshCells({ rowNodes: [this.contextMenuEvent.node], columns: [colId], force: true });
                }
            });

            // Replace row data
            this.contextMenuEvent.node.setData({
                ...this.contextMenuEvent.data,
                products: newProducts
            });

            this.gridApi.refreshCells({ force: true });
            this.cd.detectChanges();
        });
    }


    async setColumnPrice(rowData: ProductGroup): Promise<void> {
        if (!this.gridApi) return;

        const dialogRef = this.dialog.open(SetPriceDialog, {
            data: { isRow: false },
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '95vw',
        });

        dialogRef.afterClosed().subscribe(async (result: SetPriceDialogData) => {
            if (!result) return;

            const columnChange = result.change;
            const isPercentage = columnChange.dropdown === 'Percentage';
            const floatInput = parseFloat(columnChange.inputBox);
            const colHeaderName = this.contextMenuEvent.colDef.headerName as keyof Product;
            const lastLetter = colHeaderName.slice(-1);

            // collect grid nodes snapshot
            const nodes: any[] = [];
            this.gridApi.forEachNode(node => nodes.push(node));

            // setup progress UI
            this.processedCount = 0;
            this.totalCount = nodes.reduce((acc, n) => acc + (n.data?.products?.length || 0), 0);
            this.isDoneProcessing = false;
            document.body.classList.add("overlay-active");
            this.cd.detectChanges();

            // let browser render overlay first
            await new Promise(r => setTimeout(r, 0));

            const updatedRowData: any[] = new Array(nodes.length);
            const changedMap = new Map<string, any>();

            const CHUNK_ROWS = 100;

            await this.ngZone.runOutsideAngular(async () => {
                for (let start = 0; start < nodes.length; start += CHUNK_ROWS) {
                    const slice = nodes.slice(start, start + CHUNK_ROWS);

                    for (let i = 0; i < slice.length; i++) {
                        const node = slice[i];
                        const products: Product[] = node.data?.products || [];

                        const updatedProducts = products.map(p => {
                            // ensure numeric baseNumber
                            const baseNumber = Number(p[colHeaderName]) || 0;
                            const newValue = this.applyChange(columnChange.actionType, floatInput, baseNumber, isPercentage);

                            const updated = {
                                ...p,
                                [colHeaderName]: newValue
                            } as Product;

                            // record into changedMap
                            const key = `${p.StoreID}|${p.ProdNum}`;
                            const changedEntry = changedMap.get(key) || { StoreID: p.StoreID, ProdNum: p.ProdNum };
                            changedEntry[colHeaderName] = newValue;
                            changedMap.set(key, changedEntry);

                            return updated;
                        }) as [Product, ...Product[]];

                        if (updatedProducts.length === 0) {
                            updatedRowData[start + i] = node.data;
                        } else {
                            updatedRowData[start + i] = {
                                ...node.data,
                                products: [updatedProducts[0], ...updatedProducts.slice(1)]
                            };
                        }

                        this.processedCount += products.length;
                    }

                    // yield to browser, update progress
                    await new Promise<void>(resolve => {
                        this.ngZone.run(() => {
                            if (this.processedCount > this.totalCount) this.processedCount = this.totalCount;
                            this.cd.detectChanges();
                            setTimeout(() => resolve(), 0);
                        });
                    });
                }
            });

            // commit once inside Angular
            this.ngZone.run(() => {
                this.gridApi.setGridOption("rowData", updatedRowData)

                // Instead of replacing changedPrices, merge with existing
                const newChanges = Array.from(changedMap.values()).map(entry => {
                    Object.keys(entry).forEach(k => {
                        if (k.startsWith("Price")) {
                            const n = Number(entry[k]);
                            entry[k] = isNaN(n) ? entry[k] : Number(n.toFixed(2));
                        }
                    });
                    return entry;
                });

                // Merge into existing changedPrices using StoreID+ProdNum as key
                const mergedMap = new Map<string, any>();

                // First load existing changes
                (this.priceChange.changedPrices || []).forEach(entry => {
                    const key = `${entry.StoreID}|${entry.ProdNum}`;
                    mergedMap.set(key, { ...entry });
                });

                // Apply/overwrite with new changes
                newChanges.forEach(entry => {
                    const key = `${entry.StoreID}|${entry.ProdNum}`;
                    const existing = mergedMap.get(key) || { StoreID: entry.StoreID, ProdNum: entry.ProdNum };
                    mergedMap.set(key, { ...existing, ...entry });
                });

                // Final assignment
                this.priceChange.changedPrices = Array.from(mergedMap.values());

                // highlight modified column
                const targetCol = this.gridApi.getColumns()?.find((c: any) => c.colDef.headerName === colHeaderName);
                if (targetCol) {
                    const targetColId = targetCol.getColId();
                    this.gridApi.forEachNode((n) => {
                        const key = `${n.rowIndex}-${targetColId}`;
                        this.modifiedCells.set(key, true);
                    });
                    this.gridApi.refreshCells({ force: true, columns: [targetCol.getColId()] });
                }

                this.isDoneProcessing = true;
                document.body.classList.remove("overlay-active");
                this.cd.detectChanges();
            });
        });
    }

    applyChange(action, value, baseNumber, isPercentage) {
        if (typeof baseNumber !== 'number' || typeof value !== 'number') {
            throw new Error('Both value and baseNumber must be numbers');
        }

        if (action !== 'increase' && action !== 'decrease') {
            throw new Error('Action must be either "increase" or "decrease"');
        }

        // Calculate the amount to change
        let changeAmount = isPercentage ? (value / 100) * baseNumber : value;

        if (action === 'decrease') {
            changeAmount *= -1; // Reverse the change if action is 'decrease'
        }
        // Apply the change to the base number
        let result = baseNumber + changeAmount;

        // Ensure the result is not negative
        return result < 0 ? 0 : result;
    }

    private async createProductObject(productColumn, value, product): Promise<void> {
        let nameOfColumn = "Price" + productColumn
        let productNewPrice = { [nameOfColumn]: value.toFixed(2), ProdNum: product["ProdNum"], StoreID: product["StoreID"] };
        await this.addProduct(productNewPrice, true);
    }


    private groupProducts(products: Product[], groupBy: string): ProductGroup[] {
        let groupedProducts: ProductGroup[] = [];
        products.forEach(product => {
            if (groupBy === "Descript") {
                const existingProductGroup = groupedProducts.find(productGroup => {
                    const pgDescript = productGroup.Descript?.trim().toLowerCase();
                    const pDescript = product.Descript?.trim().toLowerCase();
                    return pgDescript === pDescript;
                });

                if (existingProductGroup) {
                    existingProductGroup.products.push(product);
                }
                else if (product.Descript) {
                    const newProductGroup: ProductGroup = {
                        StoreID: product.StoreID,
                        Descript: product.Descript,
                        products: [product],
                    }
                    groupedProducts.push(newProductGroup);
                }
            }
            else if (groupBy === "PLink") {
                const existingProductGroup = groupedProducts.find(productGroup => {
                    const pgPLink = productGroup.PLink?.trim().toLowerCase();
                    const pPLink = product.PLink?.trim().toLowerCase();
                    return pgPLink === pPLink;
                });

                if (existingProductGroup) {
                    existingProductGroup.products.push(product);
                }
                else if (product.PLink) {
                    const newProductGroup: ProductGroup = {
                        StoreID: product.StoreID,
                        PLink: product.PLink,
                        products: [product],
                    }
                    groupedProducts.push(newProductGroup);
                }
            }
            else {
                const newProductGroup: ProductGroup = {
                    StoreID: product.StoreID,
                    Descript: product.Descript,
                    PLink: product.PLink,
                    products: [product],
                }
                groupedProducts.push(newProductGroup);
            }
        });

        return groupedProducts;
    }

    private nullFormatter(params: ValueFormatterParams): string {
        // use (==) instead of (===) to match both null and undefined
        if (params.value == null) {
            return "N/A"
        }
        return params.value;
    }

    private groupPriceFormatter(priceType: string, params: ValueFormatterParams): any {
        const products = params.data.products as Product[];
        const firstProductPrice = products[0][priceType] as number;
        const allPricesAreEqual = products.every(p => p[priceType] === firstProductPrice)

        if (products.length > 1) {
            return allPricesAreEqual ? firstProductPrice.toFixed(2).toString() : "...";
        }
        else {
            return firstProductPrice.toFixed(2).toString();
        }
    }

    private getProductToolTip() {
        if (this.groupBy == "PLink") {
            return this.captions.numProdPLinkSelected;
        } else return;
    }


    // Save report categories to IndexedDB
    saveReportCategories(reportCategories: ReportCategory[]) {
        // Clear old categories first
        this.dbService.clear('reportCategories').subscribe(() => {
            // Save the new categories
            this.dbService.add('reportCategories', { id: 1, categories: reportCategories }).subscribe(() => {
            });
        });
    }

    // Load report categories from IndexedDB
    loadReportCategories(): Observable<ReportCategory[]> {
        return new Observable(observer => {
            this.dbService.getByKey('reportCategories', 1).subscribe((result: any) => {
                if (result && result.categories) {
                    observer.next(result.categories);
                } else {
                    observer.next([]);
                }
                observer.complete();
            });
        });
    }

}
